import { useMemo } from 'react';

import { type BaseCurrencyType } from '@pkg/types';

import { SupportedTokens } from '@app/client/common';
import { useBaseCurrencyStore, useExchangeRatesStore } from '@app/store/zustand';

import { type GetListingDataArgs, useGetActiveListingData } from '../daas';

export function getCurrencyFromChain(chain?: SupportedTokens): BaseCurrencyType {
    if (!chain) return 'usd';
    switch (chain) {
        case SupportedTokens.Aptos:
            return 'apt';
        case SupportedTokens.Polygon:
            return 'matic';
        case SupportedTokens.Ethereum:
            return 'eth';
        default:
            return 'usd';
    }
}

export function useTokenPricing(args: GetListingDataArgs) {
    const { data } = useGetActiveListingData(args);

    const chain = (args.tokenIds?.[0]?.split('.')?.[0] as SupportedTokens) || SupportedTokens.Ethereum;

    const listingDetails = data?.[chain]?.details?.[0];

    const currency = getCurrencyFromChain(chain || SupportedTokens.Ethereum);
    const formatCurrency = useBaseCurrencyStore((state) => state.formatCurrency);
    const convertCurrencyFromStore = useExchangeRatesStore((state) => state.convertCurrency);

    const weiPriceConverted = useMemo(
        () => Number(listingDetails?.wei_price) / Math.pow(10, Number(listingDetails?.currency_decimals)),
        [listingDetails?.currency_decimals, listingDetails?.wei_price],
    );

    const listingPriceUSD = useMemo(() => {
        return convertCurrencyFromStore(currency, 'usd', weiPriceConverted);
    }, [convertCurrencyFromStore, currency, weiPriceConverted]);

    const converted = convertCurrencyFromStore('usd', currency, listingPriceUSD);
    const valueFormatted = formatCurrency(currency, converted);

    return {
        ...listingDetails,
        listingPrice: weiPriceConverted,
        listingPriceUSD,
        valueFormatted,
        currency,
    };
}
